import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useEffect } from 'react'
import type {
  Product,
  SkuSpecificationsName,
} from 'src/components/restructure/product/types/product'
import { KEYWORDS_HIDDEN_COMPONENT } from 'src/components/restructure/product/sections/Sidebar/SkuSelector/DefaultSelector'
import { useMobile } from 'src/hooks/useMobile'
import { getSizeGuideModalConfig } from 'src/components/restructure/product/sections/Sidebar/SkuSelector/SizeGuide/SizeGuideModalContent'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'

import type { KitlookSkuOption } from './KitlookSkuSelectorRow'
import { KitlookSkuSelectorRow } from './KitlookSkuSelectorRow'

interface KitlookSkuSelectorProps {
  product: Product
  filters?: string[]
  isSkuSelectOpen: boolean
  setIsSkuSelectOpen: Dispatch<SetStateAction<boolean>>
  onSelect: (skuId: string) => void
  selectedSku: string | null
}

const DECATHLON_SELLER_ID = '1'

export const KitlookSkuSelector = ({
  product,
  filters = ['Cor'],
  onSelect,
  selectedSku,
  setIsSkuSelectOpen,
  isSkuSelectOpen,
}: KitlookSkuSelectorProps) => {
  const { items } = product
  const { isMobile } = useMobile()
  const modalConfig = getSizeGuideModalConfig(product, isMobile)

  const { openModal } = useModalSheet()
  const handleToggle = useCallback(() => {
    setIsSkuSelectOpen((current) => !current)
  }, [setIsSkuSelectOpen])

  const handleClose = useCallback(() => {
    setIsSkuSelectOpen(false)
  }, [setIsSkuSelectOpen])

  const filteredItems = items.filter(
    (item) => item.productId === product.productId
  )

  const firstAvailable = filteredItems.find((item) => item.isAvailable)

  const sellerId = firstAvailable?.sellerDefault.sellerId

  const openSizeGuideModal = () => {
    if (!modalConfig) {
      return
    }

    openModal(modalConfig)
  }

  const availableSpecifications = filteredItems.reduce((acc, cur) => {
    if (!cur.isAvailable) return acc

    cur.variations.forEach((variation) => {
      const variationName = variation as SkuSpecificationsName

      if (filters.includes(variationName)) return

      const values = cur[variationName]

      if (!values || KEYWORDS_HIDDEN_COMPONENT.includes(values[0])) return

      if (!acc[variationName]) acc[variationName] = []
      if (acc[variationName].some((v) => v.value === values[0])) return

      const position = product.skuSpecifications
        ?.find((specification) => specification?.field?.name === variation)
        ?.values?.find((value) => value?.name === values?.[0])?.position

      acc[variationName].push({
        value: values[0],
        skuId: cur.itemId,
        position,
      })
    })

    return acc
  }, {} as Record<SkuSpecificationsName, KitlookSkuOption[]>)

  const specKeys = Object.keys(availableSpecifications)
  const hasNoSpecifications = specKeys.length === 0

  useEffect(() => {
    if (selectedSku) return

    if (hasNoSpecifications && firstAvailable) {
      onSelect(firstAvailable.itemId)

      return
    }

    const isSingleSpec = specKeys.length === 1

    if (!isSingleSpec) return

    const specName = specKeys[0] as SkuSpecificationsName
    const options = availableSpecifications[specName]

    if (options?.length !== 1) return

    onSelect(options[0].skuId)
  }, [
    selectedSku,
    hasNoSpecifications,
    specKeys,
    availableSpecifications,
    firstAvailable,
    onSelect,
  ])

  return (
    <div className="w-full flex flex-col gap-2">
      {hasNoSpecifications && (
        <KitlookSkuSelectorRow
          label="Tamanho"
          value="único"
          hasOnlyOne
          options={[]}
          showSizeGuide={false}
          onOpenSizeGuide={openSizeGuideModal}
          isOpen={false}
          onToggle={handleToggle}
          onClose={handleClose}
          onSelect={onSelect}
          selectedSku={selectedSku}
        />
      )}

      {!hasNoSpecifications &&
        specKeys.map((specification) => {
          const options =
            availableSpecifications[specification as SkuSpecificationsName]

          const hasOnlyOneOption = options.length === 1

          const displayValue = hasOnlyOneOption
            ? options[0].value
            : items
                .find((item) => item.itemId === selectedSku)
                ?.[specification as SkuSpecificationsName]?.[0]?.trim() ??
              'Selecione o tamanho'

          const showSizeGuide = Boolean(
            specification === 'Tamanho' &&
              !hasOnlyOneOption &&
              sellerId === DECATHLON_SELLER_ID &&
              modalConfig
          )

          return (
            <KitlookSkuSelectorRow
              key={specification}
              label=""
              value={displayValue}
              hasOnlyOne={hasOnlyOneOption}
              options={options}
              showSizeGuide={showSizeGuide}
              onOpenSizeGuide={openSizeGuideModal}
              isOpen={isSkuSelectOpen}
              onToggle={handleToggle}
              onClose={handleClose}
              onSelect={onSelect}
              selectedSku={selectedSku}
            />
          )
        })}
    </div>
  )
}
